import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    Tabs,
    Tab,
    IconButton,
    useTheme,
    Chip,
    Tooltip,
    Popover,
    Stack,
    RadioGroup,
    Radio,
    FormControlLabel,
    Divider,
    Button,
} from '@mui/material';
import WhiteTooltip from '../../components/WhiteTooltip';
import { Close as CloseIcon, Settings as SettingsIcon } from '@mui/icons-material';
import AgGridTable from '../../components/table/AgGridTable';
import { ColDef, ICellRendererParams, GridOptions } from 'ag-grid-community';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CVEDetailsDrawer from '../../components/cve/CVEDetailsDrawer';
import axiosInstance from '../../axios/axios';
import { END_POINTS } from '../../shared/endpoints';
import { toast } from 'react-toastify';
import moment from "moment";
import InfoIcon from "@mui/icons-material/Info";
 
 
interface SoftwareDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    softwareData: any;
    renderSoftwarePage?: (ip: string, deviceId?: string, assetData?: any) => React.ReactNode;
}
 
const SoftwareDetailsDrawer: React.FC<SoftwareDetailsDrawerProps> = ({
    open,
    onClose,
    softwareData,
    renderSoftwarePage,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [activeTab, setActiveTab] = useState(0);
    const [cveDrawerOpen, setCveDrawerOpen] = useState(false);
    const [selectedCveData, setSelectedCveData] = useState<any>(null);
    const [assetsData, setAssetsData] = useState<any[]>([]);
    const [cveData, setCveData] = useState<any[]>([]);
    const [isLoadingAssets, setIsLoadingAssets] = useState(false);
    const [isLoadingCve, setIsLoadingCve] = useState(false);

    // Popover state for Criticality
    const [popoverAnchor, setPopoverAnchor] = useState<null | HTMLElement>(null);
    const [popoverRow, setPopoverRow] = useState<any>(null);
    const [popoverPosition, setPopoverPosition] = useState<any>(null);
    const [showRadioOptions, setShowRadioOptions] = useState<boolean>(false);
    const [selectedCriticality, setSelectedCriticality] = useState<string>("");
    const [assetCriticalityOptions, setAssetCriticalityOptions] = useState<any[]>([]);

    const [isUpdatingCriticality, setIsUpdatingCriticality] = useState(false);

    const getAssetCriticality = async () => {
        try {
            const response = await axiosInstance.get(END_POINTS.ASSETS_CRITICALITY);
            if (response?.status === 200) {
                setAssetCriticalityOptions(response?.data?.data?.dropdown);
            }
        } catch (error: any) {
            toast.error(
                error.response?.data?.message || "Something went wrong",
                { type: "error" }
            );
        }
    };

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
        event.stopPropagation();
        getAssetCriticality();
        setPopoverAnchor(event.currentTarget);
        setPopoverRow(row);
        setPopoverPosition({ top: event.clientY, left: event.clientX });
        setShowRadioOptions(false);
        setSelectedCriticality(row.criticality || "Unknown");
    };

    const handlePopoverClose = () => {
        setPopoverAnchor(null);
        setPopoverRow(null);
        setSelectedCriticality("");
        setShowRadioOptions(false);
    };

    const handleApplyCriticality = async () => {
        if (isUpdatingCriticality) return;
        
        const selectedCriticalityId = assetCriticalityOptions.find(
            (o: any) => o.label === selectedCriticality
        )?.id;

        if (!popoverRow || selectedCriticalityId === undefined) return;

        try {
            setIsUpdatingCriticality(true);
            const payload = {
                device_id: [popoverRow.device_id || popoverRow.id],
                type: "software",
                criticality: selectedCriticalityId,
            };
            
            console.log("Sending Software Criticality Payload:", payload);
            
            const response = await axiosInstance.post(
                END_POINTS.ASSETS_CRITICALITY_UPDATE,
                payload
            );

            if (response.status === 200 || response.data?.success) {
                toast.success("Criticality updated successfully");
                handlePopoverClose();
                fetchAssetsData(); // Refresh assets
            }
        } catch (error: any) {
            console.error("Error updating criticality:", error);
            toast.error(error.response?.data?.message || "Failed to update criticality");
        } finally {
            setIsUpdatingCriticality(false);
        }
    };

    const getLabelText = (value: any) => {
        const option = assetCriticalityOptions.find(
            (o: any) => o.label === value?.criticality
        );
        if (option?.label) return option.label;
        if (value?.criticality) {
            return value.criticality.charAt(0).toUpperCase() + value.criticality.slice(1).toLowerCase();
        }
        return "Unknown";
    };

    const criticalityColorsForPopover: any = {
        critical: "#B71E31",
        high: "#FF6600",
        medium: "#E0B102",
        low: "#00A61B",
        default: "#B71E31",
    };
 
    // Single Asset Software overlay state
    const [clickedAsset, setClickedAsset] = useState<any>(null);
    const [assetDrawerOpen, setAssetDrawerOpen] = useState(false);
 
    const fetchAssetsData = async () => {
        setIsLoadingAssets(true);
        try {
            if ((!softwareData?.asset_ids || softwareData.asset_ids.length === 0) && 
                (!softwareData?.inactive_asset_ids || softwareData.inactive_asset_ids.length === 0)) {
                setAssetsData([]);
                return;
            }
 

 
            const payload = {
                asset_ids: softwareData.asset_ids || [],
                inactive_asset_ids: softwareData.inactive_asset_ids || []
            };

            const response = await axiosInstance.post(END_POINTS.WMI_ASSET_DETAILS, payload);
 
            if (response.data?.results) {
                // Map the new API fields to the format expected by the table columns
                const mappedAssets = response.data.results.map((asset: any, index: number) => {
                    const validDeviceId = (asset.device_id && typeof asset.device_id === 'string' && asset.device_id.trim() !== '')
                        ? asset.device_id.trim()
                        : '';
                    return {
                        ...asset,
                        id: asset.asset_id ? `${asset.asset_id}_${index}` : `asset_${index}`,
                        device_id: validDeviceId,
                        asset_id: asset.asset_id || '',
                        device_name: asset.asset_name || '',
                        ip: asset.ip || '',
                        vendor: asset.Manufacturer || '', // Mapping domain to vendor field for now
                        model: asset.model || '',
                        version: asset.version || '',
                        status: asset.status || '',
                        os: asset.os || '',
                        last_scan: asset.detected_date || '',
                        scan_count: asset.scan_count || 0,
                        criticality: asset.criticality || '',
                    };
                });
                setAssetsData(mappedAssets);
            } else {
                setAssetsData([]);
            }
        } catch (error) {
            console.error('Error fetching assets data:', error);
            toast.error('Failed to fetch assets data');
        } finally {
            setIsLoadingAssets(false);
        }
    };
 
    const fetchTopCves = async () => {
        setIsLoadingCve(true);
        try {
            if (!softwareData?.cves_list || softwareData.cves_list.length === 0) {
                setCveData([]);
                return;
            }
 
            const payload = {
                cves: softwareData.cves_list
            };
 
            console.log("Fetching WMI CVE Lookup:", END_POINTS.WMI_CVE_LOOKUP, payload);
            const response = await axiosInstance.post(END_POINTS.WMI_CVE_LOOKUP, payload);
 
            if (response.data?.results) {
                // Map the new API fields to the format expected by the CVE table columns
                const mappedCves = response.data.results.map((cve: any) => ({
                    id: cve.cve_id || '',
                    cveId: cve.cve_id || '',
                    cvss: cve.cvss_score || '',
                    severity: cve.severity || '',
                    cisaKev: cve.cisa_kev ? 'Exploited' : 'Not Exploited',
                    epss: cve.epss_score ? `${(parseFloat(cve.epss_score) * 100).toFixed(2)}%` : '-',
                    assetCount: 0, // Asset count for specific CVE is not provided by this API
                    description: cve.description || '',
                }));
 
                // Handle missing CVEs if any (though the API usually returns all)
                const apiCveIds = new Set(mappedCves.map((c: any) => c.cveId));
                const missingCves = softwareData.cves_list
                    .filter((cveId: string) => !apiCveIds.has(cveId))
                    .map((cveId: string) => ({
                        id: cveId,
                        cveId: cveId,
                        cvss: '',
                        severity: '',
                        cisaKev: '',
                        epss: '',
                        assetCount: 0,
                        description: '',
                    }));
 
                setCveData([...mappedCves, ...missingCves]);
            } else {
                setCveData([]);
            }
 
        } catch (error) {
            console.error('Error fetching CVE data:', error);
            toast.error('Failed to fetch CVE data');
        } finally {
            setIsLoadingCve(false);
        }
    };
 
    useEffect(() => {
        if (open && softwareData) {
            setActiveTab(softwareData.openToTab || 0);
            fetchAssetsData();
            fetchTopCves();
        }
    }, [open, softwareData]);
 
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };
 
    const handleCveRowClick = async (row: any) => {
        console.log(row, 'row');
        try {
            setIsLoadingCve(true);
            const baseCveData: any = {
                cve_id: row.cveId,
                severity: row.severity || 'Unknown',
                cvss_score: row.cvss ? parseFloat(row.cvss) : 0,
                affected_assets: row.assetCount || 0,
                cisa_key: row.cisaKev === 'Exploited',
            };
 
            const resp = await axiosInstance.post("securityscan/cve/cve_summary", { cve: row.cveId });
            console.log("CVE Summary API Response:", resp);
            
            if (resp.status === 200 || resp.data?.status === 200) {
                baseCveData.summaryData = resp.data?.data?.cve_summary_details || resp.data?.cve_summary_details;
            }
 
            setSelectedCveData(baseCveData);
            setCveDrawerOpen(true);
        } catch (error) {
            console.error('Error opening CVE details:', error);
            toast.error('Failed to open CVE details');
        } finally {
            setIsLoadingCve(false);
        }
    };
 
    const handleCveChipClick = (cveId: string) => {
        // Find detailed data if already available in the grid state
        const detail = cveData.find(c => c.cveId === cveId);
        if (detail) {
            handleCveRowClick(detail);
        } else {
            // Fallback for chips without grid data yet
            handleCveRowClick({ cveId });
        }
    };
 
 
    const handleCveDrawerClose = () => {
        setCveDrawerOpen(false);
        setSelectedCveData(null);
    };
    // Assets column definitions
    const criticalityColors: Record<string, string> = {
        critical: '#B71E31',
        high: '#FF6600',
        medium: '#E0B102',
        low: '#00A61B',
    };

    const assetsColumnDefs: ColDef[] = [
        {
            field: "device_name",
            headerName: "Asset Name",
            minWidth: 100,
            filter: false,
            sortable: true,
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
            cellRenderer: (params: ICellRendererParams) => {
                const value = params.value || "";
                const criticality = params.data?.criticality;
                const barColor = criticality
                    ? criticalityColors[criticality.toLowerCase()] || '#B71E31'
                    : undefined;
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            width: "100%",
                            overflow: "hidden",
                        }}
                    >
                        {barColor && (
                                <div
                                    className="ag-criticality-bar"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePopoverOpen(e, params.data);
                                    }}
                                    style={{
                                        width: '4px',
                                        height: '32px',
                                        backgroundColor: barColor,
                                        borderRadius: '2px',
                                        marginRight: '8px',
                                        flexShrink: 0,
                                        cursor: 'pointer'
                                    }}
                                />
                        )}
                        <Tooltip
                            title={value}
                            arrow
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: "#fff",
                                        color: "#000",
                                        boxShadow: 1,
                                        fontSize: "0.775rem",
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: "#fff",
                                    },
                                },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {value}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: "ip",
            headerName: "IP ",
            width: 90,
            sortable: true,
            filter: false,
            minWidth: 90,
            cellRenderer: (params: ICellRendererParams) => (
                <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.value || ''}
                    </Typography>
                </WhiteTooltip>
            ),
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "vendor",
            headerName: "Vendor",
            sortable: true,
            filter: false,
            width: 90,
            minWidth: 90,
            cellRenderer: (params: ICellRendererParams) => {
                const colorSchemes = [
                    { bg: '#E0E7FF', text: '#1E40AF' }, // Light blue background with dark blue text
                    { bg: '#F3E8FF', text: '#7C3AED' }, // Light lavender background with medium purple text
                    { bg: '#FEF3C7', text: '#D97706' }, // Light yellow background with golden orange text
                    { bg: '#FCE7F3', text: '#BE185D' }, // Light pink background with medium pink text
                    { bg: '#FED7AA', text: '#EA580C' }, // Light peach background with bright orange text
                ];
                const colorIndex = (params.node?.rowIndex ?? 0) % colorSchemes.length;
                const scheme = colorSchemes[colorIndex];
 
                return (
                    <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                        <Chip
                            label={params.value || ''}
                            size="small"
                            sx={{
                                backgroundColor: scheme.bg,
                                color: scheme.text,
                                fontWeight: 600,
                                fontSize: 12,
                                height: 24,
                                borderRadius: 1,
                            }}
                        />
                    </WhiteTooltip>
                );
            },
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "model",
            headerName: "Model",
            sortable: true,
            filter: false,
            minWidth: 80,
            hide: true, // Hide by default
            cellRenderer: (params: ICellRendererParams) => (
                <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.value || ''}
                    </Typography>
                </WhiteTooltip>
            ),
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "os",
            headerName: "OS",
            sortable: true,
            filter: false,
            width: 80,
            minWidth: 80,
            cellRenderer: (params: ICellRendererParams) => (
                <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.value || ''}
                    </Typography>
                </WhiteTooltip>
            ),
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "version",
            headerName: "OS version",
            sortable: true,
            filter: false,
            // hide: true, // Hide OS version as it is usually included in the OS field from WMI
            width: 90,
            minWidth: 90,
            cellRenderer: (params: ICellRendererParams) => (
                <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.value || ''}
                    </Typography>
                </WhiteTooltip>
            ),
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },

        {
            field: "status",
            headerName: "Status",
            sortable: true,
            filter: false,
            width: 80,
            minWidth: 70,
            maxWidth: 100,
            cellRenderer: (params: ICellRendererParams) => {
                const value = params.value || '';
                const isActive = value.toLowerCase() === 'active';
                return (
                    <WhiteTooltip title={value} arrow placement="bottom">
                        <Chip
                            label={value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'}
                            size="small"
                            sx={{
                                backgroundColor: isActive ? '#E6FFE6' : '#FFE6E6',
                                color: isActive ? '#008000' : '#FF0000',
                                border: `1px solid ${isActive ? '#008000' : '#FF0000'}`,
                                fontWeight: 600,
                                fontSize: 12,
                                height: 24,
                                borderRadius: 2,
                            }}
                        />
                    </WhiteTooltip>
                );
            },
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
    ];
 
    // CVE column definitions
    const cveColumnDefs: ColDef[] = [
        {
            field: "id",
            headerName: "#",
            minWidth: 80,
            maxWidth: 100,
            width: 85,
            filter: false,
            valueGetter: (params: any) => params.node.rowIndex + 1,
        },
        {
            field: "cveId",
            headerName: "CVE",
            width: 150,
            filter: false,
            sortable: true,
            minWidth: 100,
            maxWidth: 200,
            cellRenderer: (params: ICellRendererParams) => (
                <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                    <Typography
                        variant="body2"
                        sx={{
                            color: isDarkMode ? "#FFFFFF" : "#000000",
                            fontWeight: 700,
                            fontSize: "12px",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.value || ''}
                    </Typography>
                </WhiteTooltip>
            ),
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "cvss",
            headerName: "CVSS",
            sortable: true,
            filter: false,
            width: 100,
            minWidth: 80,
            maxWidth: 120,
            cellRenderer: (params: ICellRendererParams) => (
                <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.value || ''}
                    </Typography>
                </WhiteTooltip>
            ),
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "severity",
            headerName: "Severity",
            sortable: true,
            filter: false,
            width: 120,
            minWidth: 120,
            maxWidth: 200,
            cellRenderer: (params: ICellRendererParams) => {
                const severity = params.value?.toLowerCase();
                if (!severity) return '';
 
                let chipColor = "";
                let bgColor = "";
 
                if (isDarkMode) {
                    switch (severity) {
                        case "critical":
                            chipColor = "#FF3D3D";
                            bgColor = "rgba(255, 61, 61, 0.18)";
                            break;
                        case "high":
                            chipColor = "#FF6B6B";
                            bgColor = "rgba(255, 107, 107, 0.15)";
                            break;
                        case "medium":
                            chipColor = "#FFA726";
                            bgColor = "rgba(255, 167, 38, 0.15)";
                            break;
                        case "low":
                            chipColor = "#FFE082";
                            bgColor = "rgba(255, 224, 130, 0.15)";
                            break;
                        default:
                            chipColor = "#9CA3AF";
                            bgColor = "rgba(156, 163, 175, 0.15)";
                    }
                } else {
                    switch (severity) {
                        case "critical":
                            chipColor = "#670004";
                            bgColor = "#67000424";
                            break;
                        case "high":
                            chipColor = "#A7000F";
                            bgColor = "#A7000F24";
                            break;
                        case "medium":
                            chipColor = "#E85800";
                            bgColor = "#E8580024";
                            break;
                        case "low":
                            chipColor = "#F2BF00";
                            bgColor = "#F2BF0024";
                            break;
                        default:
                            chipColor = "#6c6c6c";
                            bgColor = "rgba(0, 0, 0, 0.07)";
                    }
                }
 
                return (
                    <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                        <Chip
                            label={params.value || ''}
                            size="small"
                            icon={
                                <FiberManualRecordIcon
                                    fontSize="small"
                                    style={{ color: chipColor, fontSize: 14 }}
                                />
                            }
                            sx={{
                                borderRadius: 1,
                                bgcolor: bgColor,
                                color: chipColor,
                                fontWeight: 600,
                                fontSize: 12,
                                px: 1.5,
                                height: 24,
                            }}
                        />
                    </WhiteTooltip>
                );
            },
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "cisaKev",
            headerName: "CISA's KEV",
            sortable: true,
            filter: false,
            width: 100,
            minWidth: 80,
            maxWidth: 120,
            headerComponent: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>CISA's KEV</span>
                    <Tooltip title="Known Exploited Vulnerabilities">
                        <IconButton size="small">
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            cellRenderer: (params: ICellRendererParams) => {
                const isExploited = params.value?.toLowerCase() === 'exploited';
                return (
                    <WhiteTooltip title={params.value || ''} arrow placement="bottom">
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                fontSize: "12px",
                                color: isExploited ? "#DC2626" : (isDarkMode ? "#FFFFFF" : "#000000"),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                            }}
                        >
                            {params.value || ''}
                        </Typography>
                    </WhiteTooltip>
                );
            },
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
        {
            field: "epss",
            headerName: "EPSS",
            sortable: true,
            filter: false,
            width: 100,
            minWidth: 80,
            maxWidth: 120,
            headerComponent: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>EPSS</span>
                    <Tooltip title="Exploit Prediction Scoring System">
                        <IconButton size="small">
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            cellRenderer: (params: ICellRendererParams) => (
                <WhiteTooltip title={params.value || '-'} arrow placement="bottom">
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.value || '-'}
                    </Typography>
                </WhiteTooltip>
            ),
            cellStyle: {
                display: "flex",
                alignItems: "center",
            },
        },
    ];
 
    const defaultColDef: ColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
        minWidth: 100,
    };
 
    const gridOptions: GridOptions = {
        defaultColDef,
        rowHeight: 48,
        headerHeight: 48,
        suppressCellFocus: true,
        suppressRowClickSelection: true,
        rowMultiSelectWithClick: false,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        getRowId: (params) => params.data.id,
        paginationPageSizeSelector: [20, 50, 100],
    };

    const handleAssetRowClick = (rowData: any) => {
        if (rowData && (rowData.ip || rowData.device_id || rowData.asset_id)) {
            setClickedAsset(rowData);
            setAssetDrawerOpen(true);
        }
    };

    const assetsGridOptions: GridOptions = {
        ...gridOptions,
    };

    const cveGridOptions: GridOptions = {
        ...gridOptions,
    };
 
    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: '80%',
                        maxWidth: '800px',
                        backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                    },
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            padding: '16px 20px',
                            borderBottom: '1px solid #E2E2F2',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: isDarkMode ? '#FFFFFF' : '#000000',
                                    fontWeight: 600,
                                }}
                            >
                                {softwareData?.name || ''} - {softwareData?.version || ''} ({softwareData?.vendor || ''})
                            </Typography>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
 
                        <Typography
                            variant="body2"
                            sx={{
                                color: isDarkMode ? '#9CA3AF' : '#667085',
                                fontSize: '14px',
                                mt: -0.5,
                                mb: 1,
                            }}
                        >
                            Total Occurrence - {assetsData.length || softwareData?.occurrences || 0}
                        </Typography>
 
                        {/* CVE Chips List removed per request to focus on the grid, but kept if needed for quick navigation */}
                    </Box>
 
 
 
                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                        <Tabs value={activeTab} onChange={handleTabChange}>
                            <Tab label="Assets" />
                            <Tab label="CVE" />
                        </Tabs>
                    </Box>
 
                    {/* Search Bar */}
                    <Box
                        sx={{
                            padding: '0px 20px',
                        }}
                    >
                    </Box>
 
                    {/* Content */}
                    <Box sx={{ flex: 1, padding: '16px 16px', minHeight: 0, overflow: 'hidden' }}>
                        {activeTab === 0 ? (
                            <AgGridTable
                                key="assets-tab"
                                columnDefs={assetsColumnDefs}
                                data={assetsData}
                                height="calc(100vh - 200px)"
                                pagination={true}
                                paginationPageSize={20}
                                isLoading={isLoadingAssets}
                                gridOptions={assetsGridOptions}
                                onRowClick={handleAssetRowClick}
                                showSearch
                                searchPlaceholder='Search Asset'
                            />
                        ) : (
                            <AgGridTable
                                key="cve-tab"
                                columnDefs={cveColumnDefs}
                                data={cveData}
                                height="calc(100vh - 200px)"
                                pagination={true}
                                paginationPageSize={20}
                                isLoading={isLoadingCve}
                                gridOptions={cveGridOptions}
                                onRowClick={handleCveRowClick}
                                showSearch
                                searchPlaceholder='Search CVE'
                            />
                        )}
                    </Box>
                </Box>
            </Drawer>
 
            {/* CVE Details Drawer - Rendered outside the main drawer to avoid z-index issues */}
            {selectedCveData && (
                <CVEDetailsDrawer
                    open={cveDrawerOpen}
                    onClose={handleCveDrawerClose}
                    cveData={selectedCveData}
                    isLoading={isLoadingCve}
                    showBackButton={false}
                    isNavigationFromAssets={false}
                    showComponentsTab={false}
                    drawerWidth="80%"
                    drawerMaxWidth="800px"
                    assetIds={softwareData?.asset_ids}
                    inactiveAssetIds={softwareData?.inactive_asset_ids}
                    disableAssetClick={true}
                />
            )}

            {/* Single Asset Software Details Drawer */}
            {clickedAsset && (
                <Drawer
                    anchor="right"
                    open={assetDrawerOpen}
                    onClose={() => setAssetDrawerOpen(false)}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: '80%',
                            maxWidth: '800px',
                            backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                        },
                        // Ensure this nested drawer sits on top of the parent drawer
                        zIndex: (theme) => theme.zIndex.drawer + 2,
                    }}
                >
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Header */}
                        <Box
                            sx={{
                                padding: '16px 20px',
                                borderBottom: '1px solid #E2E2F2',
                                flexShrink: 0,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: isDarkMode ? '#FFFFFF' : '#000000',
                                        fontWeight: 600,
                                    }}
                                >
                                    {clickedAsset.device_name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: isDarkMode ? '#9CA3AF' : '#667085',
                                        fontSize: '14px',
                                    }}
                                >
                                    {clickedAsset.ip} |  {clickedAsset.os}
                                </Typography>
                            </Box>
                            <IconButton onClick={() => setAssetDrawerOpen(false)} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Body */}
                        <Box sx={{ flex: 1, padding: '16px 16px', minHeight: 0, overflow: 'hidden' }}>
                            {clickedAsset && renderSoftwarePage && (
                                renderSoftwarePage(
                                    clickedAsset.ip || '',
                                    clickedAsset.device_id || '',
                                    clickedAsset
                                )
                            )}
                        </Box>
                    </Box>
                </Drawer>
            )}
            {/* Asset Criticality Popover */}
            <Popover
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                anchorReference="anchorPosition"
                anchorPosition={popoverPosition || { top: 0, left: 0 }}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                PaperProps={{
                    sx: { p: 0, boxShadow: 3, borderRadius: 2, bgcolor: isDarkMode ? "#1A1B23" : "#FFFFFF", color: isDarkMode ? "#E5E7EB" : "#101828" },
                }}
                disableRestoreFocus
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        padding: "10px 13px",
                        gap: 2,
                        boxShadow: 3,
                        borderRadius: 2,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                            sx={{
                                fontFamily: "Manrope, Helvetica",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                lineHeight: "20px",
                            }}
                        >
                            {popoverRow?.device_name || popoverRow?.ip || "Unknown"} :{" "}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                bgcolor: "#f3f3f3",
                                px: 1,
                                py: 0.5,
                                borderRadius: "6px",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor:
                                        criticalityColorsForPopover[popoverRow?.criticality?.toLowerCase()] ||
                                        "goldenrod",
                                    mr: 0.5,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: "Geologica, Helvetica, sans-serif",
                                    fontWeight: 600,
                                    color:
                                        criticalityColorsForPopover[popoverRow?.criticality?.toLowerCase()] ||
                                        "goldenrod",
                                    fontSize: "12px",
                                }}
                            >
                                {getLabelText(popoverRow)}
                            </Typography>
                        </Box>
                    </Stack>
                    <Divider sx={{ width: "100%" }} />
                    <Stack spacing={2}>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            onClick={() => {
                                setShowRadioOptions(!showRadioOptions);
                            }}
                            sx={{ cursor: "pointer" }}
                        >
                            <SettingsIcon sx={{ width: 16, height: 16 }} />
                            <Typography
                                sx={{
                                    fontFamily: "Manrope, Helvetica",
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                }}
                            >
                                Set Asset Criticality
                            </Typography>
                        </Stack>
                        {showRadioOptions && (
                            <RadioGroup
                                value={selectedCriticality}
                                onChange={(e) => setSelectedCriticality(e.target.value)}
                            >
                                {assetCriticalityOptions.map((option: any) => (
                                    <FormControlLabel
                                        key={option.label}
                                        value={option.label}
                                        control={<Radio size="small" />}
                                        label={
                                            <Typography
                                                sx={{
                                                    fontFamily: "Manrope, Helvetica",
                                                    fontWeight: 500,
                                                    fontSize: "0.75rem",
                                                    opacity: 0.8,
                                                }}
                                            >
                                                {option.label}
                                            </Typography>
                                        }
                                        sx={{ marginBottom: 0.5 }}
                                    />
                                ))}
                            </RadioGroup>
                        )}
                        {showRadioOptions && (
                            <Button
                                onClick={handleApplyCriticality}
                                disabled={selectedCriticality === "" || isUpdatingCriticality}
                                variant="contained"
                                sx={{
                                    height: "21px",
                                    textTransform: "none",
                                    fontSize: "10px",
                                    fontFamily: "Roboto, Helvetica",
                                    borderRadius: "5px",
                                    bgcolor: "#4338ca",
                                    "&:hover": {
                                        bgcolor: "#3730a3",
                                    },
                                }}
                            >
                                Apply
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Popover>
        </>
    );
};

export default SoftwareDetailsDrawer;
